<template>
  <div v-loading="downloading" id="activity-stream">
    <div v-if="initialLoading">
      <HeaderLoader />
    </div>
    <div v-else>
      <div class="sm:flex sm:items-center sm:justify-between space-y-3 pb-5 sm:space-y-0">
        <div class="space-x-6 flex items-center justify-between flex-grow">
          <p class="inline title">Activity Stream</p>
          <el-button v-if="showExport" type="plain" class="border-brand-gray-blue border-2 px-4 rounded-md" @click="downloadSessions">
            <span class="text-xs text-brand-blue font-semibold">Export</span>
          </el-button>
        </div>
      </div>
      <div class="flex justify-between flex-grow mb-1">
        <div class="flex justify-start">
          <div class="max-w-lg flex items-center space-x-2">
            <el-input
              class="activity-stream-search"
              placeholder="Search for customer or feedback"
              prefix-icon="el-icon-search"
              clearable
              @clear="search"
              v-model="searchInput"
              @keyup.enter.native="search"
              id="activity-stream-search"
            ></el-input>
            <el-button :loading="loading && !!searchInput.length" type="primary" class="border-brand-blue border-2 px-4 rounded-md" @click="search">
              <span class="text-xs text-white font-semibold">Search</span>
            </el-button>
          </div>
        </div>
        <div class="flex-col flex space-y-2">
          <div class="flex items-center space-x-2">
            <DatePickerCondensed @change="handleDateChange" :dateRange="[startDate, endDate]"></DatePickerCondensed>
            <SessionFilter @update="onFilterUpdate" :include="['trial', 'offerType', 'billingInterval', 'segmentId', 'response', 'planId']" />
          </div>
        </div>
      </div>
    </div>
    <div>
      <div v-if="loading && !sessions.length" class="-ml-4">
        <SessionListLoader />
      </div>
      <div class="mt-6" v-else-if="sessions.length">
        <ul class="divide-y divide-brand-sand space-y-4 border-t border-brand-sand pb-4 border-b">
          <li v-for="session in sessions" :key="session._id" class="pt-4">
            <SessionCard :session="session" @view="(session) => $emit('view', session)" />
          </li>
        </ul>
        <div class="flex justify-center mt-8" v-if="sessions.length && this.moreSessions">
          <el-button type="primary" class="px-8 rounded-md focus:outline-none" @click="fetchNextPage" v-loading="loading">
            <span class="text-sm text-white font-semibold">Load More ↓</span>
          </el-button>
        </div>
      </div>
      <div v-else class="bg-white h-96"></div>
    </div>
  </div>
</template>

<script>
import { OFFER_TYPE, DISCOUNT_TYPE, LOAD_STATE } from '@/store/shared';
import StatService from '@/api/stats.service';
import { getDateEndpoints } from '@/helpers/date-utils';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
import SessionFilter from '@/components/CancelAnalytics/SessionFilter.vue';
import SessionCard from '@/components/CancelAnalytics/SessionCard.vue';
import { timeDay } from 'd3-time';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import SessionListLoader from '@/components/UI/Loader/SessionListLoader.vue';
import { mapGetters } from 'vuex';
import { DOWNLOAD_SESSIONS_NEXT } from '@/store/action-types';

export default {
  name: 'ActivtyStreamSessionList',
  components: {
    SessionFilter,
    DatePickerCondensed,
    SessionCard,
    SessionListLoader,
    HeaderLoader,
  },
  props: {
    showExport: {
      type: Boolean,
      default: false,
    },
    test: {
      type: Boolean,
      default: false,
    },
  },
  async created() {
    await this.fetchData();
    if (this.sessions.length < 8 && this.daysSinceOrgCreated && this.daysSinceOrgCreated > 90 && !this.searchInput) {
      this.startDate = this.orgCreatedDate;
      await this.fetchData();
    }
    this.initialLoading = false;
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -90);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, new Date()]);
    return {
      OFFER_TYPE,
      DISCOUNT_TYPE,
      filter: {},
      sessions: [],
      sessionLoadState: LOAD_STATE.UNLOADED,
      downloading: false,
      searchInput: this.$route.query.search || '',
      skip: 0,
      limit: 10,
      moreSessions: true,
      startDate,
      endDate,
      initialLoading: true,
    };
  },
  computed: {
    ...mapGetters(['daysSinceOrgCreated', 'orgCreatedDate']),
    loading() {
      return this.sessionLoadState !== LOAD_STATE.LOADED;
    },
    mode() {
      return this.test ? 'TEST' : 'LIVE';
    },
  },
  mounted() {
    if (this.$route.query.search) {
      // scroll to this div
      const el = document.getElementById('activity-stream');
      el.scrollIntoView({ behavior: 'smooth' });
    }
  },
  methods: {
    async fetchData() {
      let mappedFilter = Object.keys(this.filter).reduce((acc, k) => {
        if (this.filter[k].length === 1) {
          acc[k] = this.filter[k][0];
        }
        return acc;
      }, {});
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      if (this.searchInput.length) {
        mappedFilter = { email: this.searchInput };
      }
      if (this.test) {
        mappedFilter.mode = 'TEST';
      }
      try {
        this.sessionLoadState = LOAD_STATE.LOADING;
        let sessions;
        if (this.searchInput.length) {
          sessions = await StatService.sessionSearch({
            search: this.searchInput,
            filter: { mode: this.mode },
            options: { skip: this.skip, limit: this.limit },
          });
        } else {
          sessions = await StatService.filteredSessions({ filter: mappedFilter, options: { skip: this.skip, limit: this.limit } });
        }
        this.sessions.push(...sessions);
        this.skip += sessions.length;
        if (sessions.length < this.limit) {
          this.moreSessions = false;
        }
        this.sessionLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.log(e);
        this.sessionLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
    handleDateChange(value) {
      this.startDate = value[0];
      this.endDate = value[1];
      this.resetPagination();
      this.fetchData();
    },
    onFilterUpdate(newFilter) {
      this.filter = newFilter;
      this.resetPagination();
      this.fetchData();
    },
    resetPagination() {
      this.sessions = [];
      this.skip = 0;
      this.moreSessions = true;
    },
    fetchNextPage() {
      this.fetchData();
    },
    search() {
      this.resetPagination();
      this.fetchData();
    },
    async downloadSessions() {
      let mappedFilter = Object.keys(this.filter).reduce((acc, k) => {
        if (this.filter[k].length === 1) {
          acc[k] = this.filter[k][0];
        }
        return acc;
      }, {});
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      if (this.searchInput.length) {
        mappedFilter = { email: this.searchInput };
      }
      try {
        this.downloading = true;
        await this.$store.dispatch(DOWNLOAD_SESSIONS_NEXT, { filter: mappedFilter });
      } catch (e) {
        console.log(e.toString());
        this.$message({
          message: e.toString(),
          type: 'error',
          duration: 6000,
        });
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.customer-details {
  @apply list-none;
  li {
    display: inline;
  }
  li + li::before {
    content: '  ·  ';
  }
}
</style>

<style lang="scss">
.activity-stream-search {
  @apply shadow-sm;
  @apply rounded-md;
  width: 20rem;
  .el-input__icon {
    @apply text-brand-gray-blue;
  }
  .el-input__inner {
    @apply border-brand-gray-blue;
    @apply rounded-lg;
    border-width: 2px;
    line-height: 44px;
    height: 44px;
    &::placeholder {
      @apply text-brand-gray-blue;
    }
  }
}
</style>
